/**
 * Authenticated list of the account's standing memberships ("mensalista")
 * across arenas (`/mensalistas`). Mirrors `PlansPage` (training-center plans);
 * each card opens the membership payment screen.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { analyticsService } from '@meiuca/analytics'
import { ANALYTICS_EVENTS, SCREEN_NAMES } from '@/constants/analyticsEvents'
import { H4, Body } from '@meiuca/ui-components'
import { Loader2, Repeat } from 'lucide-react'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { VenueMembershipCard } from '@/components/VenueMembershipCard'
import { useAuth } from '@/contexts/AuthContext'
import { LoginRequiredEmptyState } from '@/components/LoginRequiredEmptyState'
import {
  venueMembershipService,
  type VenueMembershipItem,
  type VenueMembershipListScope
} from '@/services/venueMembershipService'

export function MembershipsPage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()

  const [memberships, setMemberships] = useState<VenueMembershipItem[]>([])
  const [scope, setScope] = useState<VenueMembershipListScope>('active')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    analyticsService.trackScreenView(SCREEN_NAMES.MEMBERSHIPS)
  }, [])

  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) {
      analyticsService.track(ANALYTICS_EVENTS.MEMBERSHIP_LOGIN_REQUIRED, {})
    }
  }, [isAuthLoading, isAuthenticated])

  useEffect(() => {
    if (isAuthenticated) {
      loadMemberships()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, scope])

  const loadMemberships = async () => {
    try {
      setLoading(true)
      setError(null)
      setMemberships(await venueMembershipService.listMemberships(scope))
    } catch (err) {
      console.error('Error loading memberships:', err)
      setError('Erro ao carregar seus horários fixos. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background-primary">
      <MarketplaceHeader />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <H4 className="text-text-primary font-semibold mb-2">Meus horários fixos</H4>
          <Body className="text-text-secondary">Suas mensalidades de horário fixo nas arenas</Body>
        </div>

        {isAuthenticated && (
          <div className="mb-6 flex gap-2">
            <ScopeTab active={scope === 'active'} onClick={() => setScope('active')}>
              Ativos
            </ScopeTab>
            <ScopeTab active={scope === 'ended'} onClick={() => setScope('ended')}>
              Finalizados
            </ScopeTab>
          </div>
        )}

        {isAuthLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
          </div>
        ) : !isAuthenticated ? (
          <LoginRequiredEmptyState
            title="Entre para ver seus horários fixos"
            description="Faça login para acompanhar e pagar suas mensalidades nas arenas."
          />
        ) : loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
            <Body className="text-text-secondary mt-4">Carregando...</Body>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Body className="text-feedback-error mb-4">{error}</Body>
            <button
              onClick={loadMemberships}
              className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        ) : memberships.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 mb-4 rounded-full bg-background-secondary flex items-center justify-center">
              <Repeat className="w-8 h-8 text-text-tertiary" />
            </div>
            <H4 className="text-text-primary mb-2">
              {scope === 'ended' ? 'Nenhum horário fixo finalizado' : 'Nenhum horário fixo'}
            </H4>
            <Body className="text-text-secondary text-center max-w-md">
              {scope === 'ended'
                ? 'Quando um horário fixo terminar, ele aparece aqui.'
                : 'Você não tem mensalidades de horário fixo ativas. Se a arena cadastrou você com outro telefone, fale com ela para vincular à sua conta.'}
            </Body>
          </div>
        ) : (
          <div className="space-y-4">
            {memberships.map((membership, index) => (
              <VenueMembershipCard
                key={membership.id}
                membership={membership}
                position={index}
                onClick={() => {
                  analyticsService.track(ANALYTICS_EVENTS.MEMBERSHIP_CARD_CLICKED, {
                    membership_id: membership.id,
                    position: index,
                    outstanding_count: membership.outstandingPayments.length
                  })
                  navigate(`/mensalistas/${membership.id}`)
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function ScopeTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
        active
          ? 'bg-brand-primary text-white'
          : 'bg-background-secondary text-text-secondary hover:text-text-primary'
      }`}
    >
      {children}
    </button>
  )
}
